import { z } from 'zod';
import { RFC_MORAL_REGEX } from '../utils/index';
import { pastDateField } from './fields';

// ═══════════════════════════════════════════════════════════════
// ONBOARDING PERSONA MORAL (PM) — Bloque empresa (F4, plan persona-moral)
// Schemas de validación para datos y domicilio de la empresa.
// Ver docs/plans/persona-moral/06-implementation-phases.md (F4).
// ═══════════════════════════════════════════════════════════════

/**
 * RFC de persona moral: SIEMPRE 12 caracteres (3 letras + 6 dígitos de
 * fecha + 3 de homoclave). A diferencia del RFC de persona física (13,
 * 4 letras), aquí la raíz es de 3 letras. Mantener separado del `rfcSchema`
 * genérico (12-13) para validar correctamente el caso PM.
 */
export const rfcMoralSchema = z
  .string()
  .trim()
  .toUpperCase()
  .length(12, 'El RFC de empresa debe tener 12 caracteres')
  .regex(RFC_MORAL_REGEX, 'El formato del RFC de empresa no es válido');

// ───────────────────────────────────────────────────────────────
// Datos de la empresa (screen 10): razón social + RFC + régimen fiscal
// ───────────────────────────────────────────────────────────────

export const companyDataSchema = z.object({
  /** Razón social (→ `razon_social` en el wire). */
  legalName: z.string().trim().min(1, 'La razón social es requerida'),
  /** RFC con homoclave (→ `rfc`). */
  rfcWithHomoclave: rfcMoralSchema,
  /**
   * Régimen fiscal (→ `tax_regime_code`). Opcional: el catálogo SCIAN/régimen
   * puede no estar disponible aún en el BE, en cuyo caso el form cae a input
   * de texto libre y este campo queda vacío sin bloquear el avance.
   */
  taxRegime: z.string().trim().optional(),
});

// ───────────────────────────────────────────────────────────────
// Domicilio de la empresa (screen 11). El BE valida + guarda en una sola
// call (no hay lookup/verify separados — ver 07-delta). `country` sin _code.
// ───────────────────────────────────────────────────────────────

export const companyAddressSchema = z.object({
  street: z.string().trim().min(1, 'La calle es requerida'),
  exteriorNumber: z.string().trim().min(1, 'El número exterior es requerido'),
  interiorNumber: z.string().trim().optional(),
  neighborhood: z.string().trim().min(1, 'La colonia es requerida'),
  municipality: z.string().trim().min(1, 'El municipio es requerido'),
  state: z.string().trim().min(1, 'El estado es requerido'),
  postalCode: z
    .string()
    .trim()
    .length(5, 'El código postal debe tener 5 dígitos')
    .regex(/^\d+$/, 'El código postal solo debe contener números'),
  country: z.string().trim().min(1, 'El país es requerido').default('MX'),
});

// ───────────────────────────────────────────────────────────────
// Actividad económica SCIAN (GAP 1, contrato #412). Cierra el bloque
// company_data: el BE exige `scian_code` + `economic_activity` (catálogo SCIAN
// INEGI). Mientras el catálogo no esté disponible en el BE, el form captura el
// código como texto y el BE valida server-side (misma degradación grácil que el
// régimen fiscal). El código SCIAN INEGI es numérico; el BE exige 5–6 dígitos
// (ONB_SCIAN_001) y valida contra el catálogo INEGI (ONB_SCIAN_003).
// ───────────────────────────────────────────────────────────────

export const companyScianSchema = z.object({
  /** Código SCIAN INEGI (→ `scian_code`). Numérico, 4–6 dígitos. */
  scianCode: z
    .string()
    .trim()
    .min(1, 'El código SCIAN es requerido')
    .regex(/^\d{5,6}$/, 'El código SCIAN debe tener 5 o 6 dígitos'),
  /** Descripción de la actividad económica (→ `economic_activity`). */
  economicActivity: z.string().trim().min(1, 'La actividad económica es requerida'),
  /**
   * Maestro/detalle del selector (UI-only): sector y subsector SCIAN con los
   * que se acotó la clase. El BE no los recibe — los deriva de la clase contra
   * el catálogo oficial — pero se conservan para restaurar el selector al
   * navegar entre steps.
   */
  scianSector: z.string().trim().optional(),
  scianSubsector: z.string().trim().optional(),
});

// ───────────────────────────────────────────────────────────────
// Datos del acta constitutiva (bloque kyb_validation). Se captura DESPUÉS de
// subir el acta, pre-llenado con lo que devuelva el OCR. El BE exige los cinco
// primeros campos (`ONB_ACTA_REQUIRED_FIELDS`) y valida que la fecha de
// constitución sea real y no futura (`ONB_ACTA_DATE_INVALID`).
//
// `powerInArticles` es "¿el poder del representante consta en el acta?". Su
// respuesta NO decide sola si se pide el poder notarial: eso lo dicta
// `power_of_attorney_required` en la respuesta del BE, que es la señal
// autoritativa. Si se omite, el BE asume `false`.
// ───────────────────────────────────────────────────────────────

export const companyActaDataSchema = z.object({
  /** Número de escritura pública (→ `escritura_number`). Alfanumérico: admite comas. */
  escrituraNumber: z.string().trim().min(1, 'El número de escritura es requerido'),
  /** Nombre del notario (→ `notary_name`). */
  notaryName: z.string().trim().min(1, 'El nombre del notario es requerido'),
  /** Número de la notaría (→ `notary_number`). */
  notaryNumber: z.string().trim().min(1, 'El número de notaría es requerido'),
  /**
   * Fecha de constitución (→ `constitution_date`). Se captura como `DD/MM/YYYY`
   * y el adapter la convierte a `YYYY-MM-DD` antes de mandarla.
   */
  constitutionDate: pastDateField({
    requiredMessage: 'La fecha de constitución es requerida',
    invalidMessage: 'La fecha de constitución no es una fecha válida',
    futureMessage: 'La fecha de constitución no puede ser futura',
  }),
  /** Nombre del representante legal que aparece en el acta (→ `legal_rep_name`). */
  legalRepName: z.string().trim().min(1, 'El nombre del representante legal es requerido'),
  /** ¿El poder consta en el acta? (→ `power_in_articles`). Default false. */
  powerInArticles: z.boolean().default(false),
});

// ═══════════════════════════════════════════════════════════════
// Tipos inferidos
// ═══════════════════════════════════════════════════════════════

export type CompanyDataFormData = z.infer<typeof companyDataSchema>;
export type CompanyAddressFormData = z.infer<typeof companyAddressSchema>;
export type CompanyScianFormData = z.infer<typeof companyScianSchema>;
export type CompanyActaDataFormData = z.infer<typeof companyActaDataSchema>;
